import { useCallback, useEffect, useState } from 'react';
import {
  apiErrorMessage,
  fetchPerpetualDeposits,
  fetchPerpetualStakes,
  updatePerpetualSchedule,
} from '../../utils/api';
import { formatDate } from '../../utils/formatDate';
import {
  formatPending,
  formatTokens,
  projectPending,
} from '../../utils/perpetual';
import { signScheduleChange } from '../../utils/signSchedule';
import { useNotify } from '../notifications/NotificationsProvider';
import { useWallet } from '../wallet/WalletProvider';
import './PerpetualList.css';

/**
 * Реестр бессрочных позиций.
 *
 * Счётчик «Накоплено сейчас» тикает раз в секунду, но в блокчейн панель за ним
 * не ходит: бэкенд раз в минуту снимает `perpetualPending` и отдаёт снимок
 * вместе с временем блока, а здесь значение доращивается по формуле контракта
 * (`utils/perpetual.js`). Один таймер на всю таблицу, а не на строку.
 *
 * «Клейм каждые» правится прямо в строке. Изменение уходит на бэкенд, но
 * подписывается кошельком: по подписи бэкенд проверяет `hasRole(ADMIN_ROLE)` в
 * контракте. Отдельного окна подтверждения нет — кошелёк и так показывает
 * подписываемый текст целиком, а изменение обратимо и не стоит газа.
 */

/** Как часто перечитывать список: снимок на бэкенде живёт минуту. */
const REFRESH_MS = 30_000;

const shortAddress = (address) =>
  address ? `${address.slice(0, 6)}…${address.slice(-4)}` : '—';

/**
 * Строка расписания в таблице.
 *
 * Значение правится по Enter или уходу фокуса, а не по каждому нажатию: иначе
 * набор «30» отправил бы сначала «3». Пока запрос идёт, поле заблокировано —
 * два запроса подряд разошлись бы с тем, что показано.
 */
const ScheduleCell = ({ position, onSaved }) => {
  const notify = useNotify();
  const { isReady } = useWallet();
  const schedule = position.schedule;
  const [value, setValue] = useState(
    schedule ? String(schedule.periodDays) : '',
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(schedule ? String(schedule.periodDays) : '');
  }, [schedule]);

  if (!schedule) {
    // Расписание заводит синхронизация вместе с позицией. Если его нет, значит
    // взнос ещё не разобран — состояние временное, и врать про «каждые 30
    // дней» нельзя.
    return <span className="perpetual_schedule__missing">не заведено</span>;
  }

  /**
   * Отказ в кошельке — решение оператора, а не сбой: отдельным текстом и без
   * красной карточки. Всё остальное показывается как ошибка.
   */
  const reportFailure = (error, what) => {
    console.error(what, error);

    if (error.code === 'ACTION_REJECTED' || error.code === 4001) {
      notify.info('Подпись отклонена в кошельке — изменение не сохранено');
      return;
    }

    notify.error(`${what}: ${apiErrorMessage(error)}`);
  };

  /** Без подключённого кошелька подписывать нечем, а бэкенд ответит 401. */
  const requireWallet = () => {
    if (isReady) return true;

    notify.error(
      'Подключите кошелёк в сети BSC: изменение подписывается и принимается только от ADMIN_ROLE.',
    );
    return false;
  };

  const save = async () => {
    const periodDays = Number(value);

    if (String(schedule.periodDays) === value.trim()) return;

    if (!Number.isInteger(periodDays) || periodDays < 1 || periodDays > 365) {
      notify.error('Периодичность — целое число от 1 до 365 дней');
      setValue(String(schedule.periodDays));
      return;
    }

    if (!requireWallet()) {
      setValue(String(schedule.periodDays));
      return;
    }

    setSaving(true);

    try {
      const changes = { periodDays };
      const auth = await signScheduleChange(position.address, changes);
      const updated = await updatePerpetualSchedule(
        position.address,
        changes,
        auth,
      );

      notify.success(
        `Выплаты для ${shortAddress(position.address)} — раз в ${updated.periodDays} дн.`,
      );
      onSaved(position.address, updated);
    } catch (error) {
      reportFailure(error, 'Не удалось сохранить расписание');
      setValue(String(schedule.periodDays));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async () => {
    if (!requireWallet()) return;

    setSaving(true);

    try {
      const changes = { enabled: !schedule.enabled };
      const auth = await signScheduleChange(position.address, changes);
      const updated = await updatePerpetualSchedule(
        position.address,
        changes,
        auth,
      );

      notify.success(
        updated.enabled
          ? `Автоклейм для ${shortAddress(position.address)} включён`
          : `Автоклейм для ${shortAddress(position.address)} выключен`,
      );
      onSaved(position.address, updated);
    } catch (error) {
      reportFailure(error, 'Не удалось переключить автоклейм');
    } finally {
      setSaving(false);
    }
  };

  return (
    <span className="perpetual_schedule">
      <input
        type="text"
        inputMode="numeric"
        className="perpetual_schedule__days"
        value={value}
        disabled={saving}
        onClick={(event) => event.stopPropagation()}
        onChange={(event) => setValue(event.target.value.replace(/\D/g, ''))}
        onBlur={save}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur();
          if (event.key === 'Escape') setValue(String(schedule.periodDays));
        }}
        aria-label="Периодичность выплат в днях"
      />
      <span className="perpetual_schedule__unit">дн.</span>
      <button
        type="button"
        className={
          schedule.enabled
            ? 'perpetual_schedule__toggle'
            : 'perpetual_schedule__toggle perpetual_schedule__toggle--off'
        }
        disabled={saving}
        onClick={(event) => {
          event.stopPropagation();
          void toggle();
        }}
        title={
          schedule.enabled
            ? 'Автоклейм включён — нажмите, чтобы выключить'
            : 'Автоклейм выключен — нажмите, чтобы включить'
        }
      >
        {schedule.enabled ? 'вкл' : 'выкл'}
      </button>
    </span>
  );
};

/** Раскрытая строка: история взносов по адресу. */
const DepositsRow = ({ address, colSpan }) => {
  const [deposits, setDeposits] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const rows = await fetchPerpetualDeposits({ address });
        if (!cancelled) setDeposits(rows);
      } catch (loadError) {
        console.error('Не удалось загрузить взносы:', loadError);
        if (!cancelled) setError(apiErrorMessage(loadError));
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [address]);

  return (
    <tr className="perpetual_deposits_row">
      <td colSpan={colSpan}>
        {error && <span className="perpetual_deposits__error">{error}</span>}
        {!error && deposits === null && <span>Загрузка взносов...</span>}
        {deposits?.length === 0 && <span>Взносов не найдено</span>}

        {deposits?.length > 0 && (
          <table className="perpetual_deposits">
            <thead>
              <tr>
                <th>Дата</th>
                <th>Сумма</th>
                <th>Тело после взноса</th>
                <th>Кто вносил</th>
                <th>Транзакция</th>
              </tr>
            </thead>
            <tbody>
              {deposits.map((deposit) => (
                <tr key={`${deposit.txHash}-${deposit.logIndex}`}>
                  <td>{formatDate(deposit.depositedAt)}</td>
                  <td>{formatTokens(deposit.amount)} EMIVN</td>
                  <td>{formatTokens(deposit.principalAfter)} EMIVN</td>
                  <td>
                    {deposit.stakedBy === 'self' ? (
                      <span className="staked_by_self">пользователь</span>
                    ) : (
                      <a
                        href={`https://bscscan.com/address/${deposit.sender}`}
                        target="_blank"
                        rel="noreferrer"
                        title={deposit.sender}
                      >
                        {shortAddress(deposit.sender)}
                      </a>
                    )}
                  </td>
                  <td>
                    <a
                      href={`https://bscscan.com/tx/${deposit.txHash}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      ссылка
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </td>
    </tr>
  );
};

const COLUMNS = 7;

const PerpetualList = ({ refreshKey }) => {
  const { isReady } = useWallet();
  const [positions, setPositions] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(null);
  // Один тик на всю таблицу: каждой строке свой таймер — это N таймеров и N
  // перерисовок в секунду вместо одной.
  const [now, setNow] = useState(() => Date.now());

  const load = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      setPositions(await fetchPerpetualStakes());
    } catch (fetchError) {
      console.error('Ошибка загрузки бессрочных позиций:', fetchError);
      setError(apiErrorMessage(fetchError));
      setPositions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  // Снимок на бэкенде обновляется раз в минуту; перечитываем чаще, чтобы
  // локальное доращивание не успевало заметно разойтись с контрактом.
  useEffect(() => {
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleScheduleSaved = (address, schedule) => {
    setPositions((current) =>
      current.map((position) =>
        position.address === address ? { ...position, schedule } : position,
      ),
    );
  };

  const filtered = positions.filter((position) =>
    position.address.toLowerCase().includes(search.toLowerCase()),
  );

  const emptyMessage = () => {
    if (loading) return 'Загрузка реестра...';
    if (positions.length === 0) return 'Бессрочных позиций нет';
    return 'По заданному поиску ничего не найдено';
  };

  return (
    <div className="perpetual_container">
      <div className="search_container">
        <input
          type="text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Поиск по адресу"
        />
        {!isReady && (
          <span className="perpetual_readonly">
            только просмотр: расписание правится с подключённого кошелька
            ADMIN_ROLE
          </span>
        )}
      </div>

      <table>
        <thead>
          <tr>
            <th>Адрес</th>
            <th>Тело</th>
            <th>Накоплено сейчас</th>
            <th>Всего выплачено</th>
            <th>Клейм каждые</th>
            <th>Следующий клейм</th>
            <th className="button_column">Взносы</th>
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 && (
            <tr className="table_message_row">
              <td colSpan={COLUMNS} className="table_message">
                {error ? (
                  <>
                    <span className="table_message__error">
                      Не удалось загрузить реестр: {error}
                    </span>
                    <button
                      type="button"
                      className="retry_button"
                      onClick={load}
                      disabled={loading}
                    >
                      Повторить
                    </button>
                  </>
                ) : (
                  emptyMessage()
                )}
              </td>
            </tr>
          )}

          {filtered.map((position) => {
            const pending = projectPending(position, now);
            const isExpanded = expanded === position.address;

            return [
              <tr
                key={position.address}
                onClick={() =>
                  setExpanded(isExpanded ? null : position.address)
                }
              >
                <td>
                  <a
                    href={`https://bscscan.com/address/${position.address}`}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(event) => event.stopPropagation()}
                  >
                    {position.address}
                  </a>
                </td>
                <td>{formatTokens(position.principal)}</td>
                <td className="perpetual_pending">{formatPending(pending)}</td>
                <td>{formatTokens(position.totalClaimed)}</td>
                <td>
                  <ScheduleCell
                    position={position}
                    onSaved={handleScheduleSaved}
                  />
                </td>
                <td>
                  {position.schedule ? (
                    formatDate(position.schedule.nextClaimAt)
                  ) : (
                    <span className="perpetual_schedule__missing">—</span>
                  )}
                </td>
                <td>
                  <button type="button" className="btn">
                    {isExpanded ? 'Свернуть' : 'Показать'}
                  </button>
                </td>
              </tr>,
              isExpanded && (
                <DepositsRow
                  key={`${position.address}-deposits`}
                  address={position.address}
                  colSpan={COLUMNS}
                />
              ),
            ];
          })}
        </tbody>
      </table>
    </div>
  );
};

export default PerpetualList;
